import * as React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { IdentifierInput } from '../components/IdentifierInput';
import { useLogin, type LoginValues } from '../hooks/useLogin';
import { getLoginFlow, createLogoutFlow, performLogout } from '@/lib/api/kratos';
import type { LoginFlow } from '@/lib/api/kratos';
import { discoverSso, submitOidcLoginForm } from '@/lib/api/sso';
import { useAuthStore } from '@/store/authStore';
import { useSettingsStore } from '@/store/settingsStore';

// Define AuthMethod type locally since we're not using the MethodSelection component
type AuthMethod = {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
};

// Simple password form component
function PasswordForm({ 
  onSubmit, 
  disabled
}: { 
  onSubmit: (password: string) => void;
  disabled: boolean;
}) {
  const [password, setPassword] = React.useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(password);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <div className="text-sm font-medium">Password</div>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={disabled}
          required
          placeholder="Enter your password"
        />
      </div>
      <Button type="submit" className="w-full" disabled={disabled || !password}>
        {disabled ? 'Signing in...' : 'Sign In'}
      </Button>
    </form>
  );
}

// Declare global functions from Kratos WebAuthn script
declare global {
  interface Window {
    oryPasskeyLogin?: () => void;
    oryPasskeyLoginAutocompleteInit?: () => void;
  }
}

/**
 * LoginPage Component
 * 
 * Handles user authentication using Ory Kratos flows
 * Supports multiple authentication methods and dynamic form rendering
 */
export function LoginPage() {
  const [searchParams] = useSearchParams();
  const flowId = searchParams.get('flow');
  const [selectedMethod, setSelectedMethod] = React.useState<string>('password');
  const [step, setStep] = React.useState<'identify' | 'choose_method' | 'authenticate'>('identify');
  const [email, setEmail] = React.useState<string>('');
  const [showGenericError, setShowGenericError] = React.useState<boolean>(false);
  const [showUi, setShowUi] = React.useState<boolean>(false);
  const [checkingSession, setCheckingSession] = React.useState<boolean>(true);
  // Set immediately before submitting the OIDC form so the user sees a
  // spinner instead of the email form during the redirect chain
  // (dashboard → Kratos → Hydra → Orchestrator → Polis → IdP → ...).
  // Browsers keep painting the current DOM until the next document is
  // ready, so this state stays visible for the full inflight chain.
  const [redirectingToSso, setRedirectingToSso] =
    React.useState<boolean>(false);
  
  const loginMutation = useLogin();
  const { setSession, clearSession } = useAuthStore();
  // Holds the email of the currently authenticated identity (if any).
  // Used by handleIdentifierSubmit to decide between fast-path (same
  // user retypes their own email → redirect to /) vs logout-and-reauth
  // (different user typed → log out current session before initiating
  // the new SSO flow). See bug fix plan in
  // wya-polis-cdk/docs/sso-implementation-plan.md (V1 hotfix).
  const [existingSessionEmail, setExistingSessionEmail] = React.useState<
    string | null
  >(null);

  // Session check on mount.
  //
  // Two access paths land here:
  //   - /auth/login  (no flow param)            ← typical entry, fast-path
  //   - /auth/login?flow=...  (Kratos flow id)  ← explicit re-auth
  //
  // Behavior:
  //   - Active session + no flowId → redirect to '/' (dashboard). Without
  //     this fast-path Kratos's `/self-service/login/browser` sees the
  //     active session and redirects back here, creating an infinite
  //     "checking session → redirecting to login" loop.
  //   - Active session + flowId    → keep the form open so a DIFFERENT
  //     user can authenticate. handleIdentifierSubmit logs out the
  //     existing session if the typed email doesn't match.
  //   - No session                 → continue with the standard Kratos
  //     flow (create one if needed).
  React.useEffect(() => {
    const checkSession = async () => {
      console.log('LoginPage: Checking existing session');
      setCheckingSession(true);

      let hasActiveSession = false;
      try {
        const kratosUrl = import.meta.env.VITE_IDENTITY_PUBLIC_URL;
        const response = await fetch(`${kratosUrl}/sessions/whoami`, {
          method: 'GET',
          credentials: 'include',
        });

        if (response.ok) {
          const sessionData = await response.json();
          if (sessionData.active) {
            hasActiveSession = true;
            setSession(sessionData);
            const email = sessionData?.identity?.traits?.email as
              | string
              | undefined;
            if (email) setExistingSessionEmail(email.toLowerCase().trim());
          }
        }
      } catch (error) {
        console.log('LoginPage: Session check failed (expected for logged out users):', error);
      }

      // Fast path: active session and no explicit flow → land in the
      // dashboard. Anyone wanting to log in as someone else must hit
      // /auth/login?flow=... which keeps the form open below.
      if (hasActiveSession && !flowId) {
        console.log('LoginPage: Active session + no flowId — redirecting to /');
        window.location.href = '/';
        return;
      }

      setCheckingSession(false);

      // No session and no flow → ask Kratos to create one.
      if (!flowId) {
        console.log('LoginPage: No flowId found, redirecting to Kratos to create flow');
        const kratosUrl = import.meta.env.VITE_IDENTITY_PUBLIC_URL;
        window.location.href = `${kratosUrl}/self-service/login/browser`;
        return;
      }

      setShowUi(true);
    };

    checkSession();
  }, [flowId, setSession]);

  // If there's already a session for a DIFFERENT email, terminate it
  // before initiating a new login. Reuses the same Kratos logout helpers
  // the UserMenu calls.
  const ensureCleanSessionFor = async (
    requestedEmail: string,
  ): Promise<'fast_path_same_user' | 'proceed'> => {
    if (!existingSessionEmail) return 'proceed';
    const target = requestedEmail.toLowerCase().trim();
    if (existingSessionEmail === target) return 'fast_path_same_user';

    console.log('LoginPage: existing session belongs to another user — logging out first', {
      existing: existingSessionEmail,
      requested: target,
    });
    try {
      const lf = await createLogoutFlow();
      await performLogout(lf.logout_token);
    } catch (err) {
      console.error('LoginPage: logout before re-auth failed (continuing anyway)', err);
    }
    clearSession();
    useSettingsStore.getState().clearAllContext();
    setExistingSessionEmail(null);
    return 'proceed';
  };

  // Fetch existing login flow (only if we have a flowId)
  const { data: flow, isLoading, error, refetch } = useQuery({
    queryKey: ['loginFlow', flowId],
    queryFn: async (): Promise<LoginFlow> => {
      if (!flowId) {
        throw new Error('No flow ID available');
      }
      
      try {
        return await getLoginFlow(flowId);
      } catch (error) {
        console.error('Failed to get login flow:', error);
        // If flow is expired or invalid, redirect to create a new one
        const kratosUrl = import.meta.env.VITE_IDENTITY_PUBLIC_URL;
        window.location.href = `${kratosUrl}/self-service/login/browser`;
        throw error;
      }
    },
    enabled: !!flowId, // Only run if we have a flowId
    retry: false, // Don't retry, redirect instead
    staleTime: 0, // Always fetch fresh flow data
  });

  // Load WebAuthn script when flow is available
  React.useEffect(() => {
    if (!flow) return;

    // Check if we have a WebAuthn script node and load it
    const webauthnNode = flow.ui.nodes.find(node => 
      node.type === 'script' && node.group === 'webauthn'
    );

    if (webauthnNode && !document.getElementById('webauthn_script')) {
      console.log('Loading WebAuthn script from flow...');
      const script = document.createElement('script');
      script.id = 'webauthn_script';
      script.src = webauthnNode.attributes.src as string;
      script.async = webauthnNode.attributes.async as boolean;
      script.crossOrigin = webauthnNode.attributes.crossorigin as string;
      if (webauthnNode.attributes.integrity) {
        script.integrity = webauthnNode.attributes.integrity as string;
      }
      if (webauthnNode.attributes.referrerpolicy) {
        script.referrerPolicy = webauthnNode.attributes.referrerpolicy as string;
      }
      if (webauthnNode.attributes.nonce) {
        script.nonce = webauthnNode.attributes.nonce as string;
      }
      
      document.head.appendChild(script);
      console.log('WebAuthn script added to document head');
    }
  }, [flow]);

  // Determine current step based on flow state and handle security errors
  React.useEffect(() => {
    if (!flow) return;

    console.log('Flow state changed:', {
      flowId: flow.id,
      nodes: flow.ui.nodes.map(n => ({ type: n.type, group: n.group, name: n.attributes.name, value: n.attributes.value, messages: n.messages })),
      messages: flow.ui.messages,
    });

    // Debug: Check for error messages
    console.log('Checking for error 4000037...');
    const globalError = flow.ui.messages?.find((msg: any) => msg.id === 4000037);
    const nodeError = flow.ui.nodes?.find((node: any) => 
      node.messages?.some((msg: any) => msg.id === 4000037)
    );
    console.log('Global error 4000037:', globalError);
    console.log('Node error 4000037:', nodeError);

    // Check for account not found error (4000037) for security handling
    // Check both global messages and node-specific messages
    const hasAccountNotFoundError = 
      flow.ui.messages?.some((msg: any) => msg.id === 4000037) ||
      flow.ui.nodes?.some((node: any) => 
        node.messages?.some((msg: any) => msg.id === 4000037)
      );
    
    if (hasAccountNotFoundError) {
      console.log('Account not found error detected - showing generic error for security');
      setShowGenericError(true);
      setStep('identify');
      return; // Don't proceed with normal flow logic
    }

    // Check if we have method buttons (excluding identifier_first)
    const methodButtons = flow.ui.nodes.filter(node => 
      node.type === 'input' && 
      ['button', 'submit'].includes(node.attributes.type as string) &&
      node.attributes.value !== 'identifier_first'
    );

    // Check if we have an identifier field with a value (user has entered email)
    const identifierNode = flow.ui.nodes.find(node => 
      node.attributes.name === 'identifier' && 
      node.attributes.value
    );

    if (methodButtons.length > 0 && identifierNode) {
      // We have methods available and user has entered email
      setStep('choose_method');
      setEmail(String(identifierNode.attributes.value));
    } else if (email && selectedMethod && selectedMethod !== 'password') {
      // User has selected a method, show authentication form
      setStep('authenticate');
    } else {
      // Default to identifier input
      setStep('identify');
    }
  }, [flow, email, selectedMethod]);

  // Extract available authentication methods from flow
  const availableMethods = React.useMemo((): AuthMethod[] => {
    if (!flow || step !== 'choose_method') return [];

    const methods: AuthMethod[] = [];
    
    // Get method buttons (excluding identifier_first)
    const methodButtons = flow.ui.nodes.filter(node => 
      node.type === 'input' && 
      ['button', 'submit'].includes(node.attributes.type as string) &&
      node.attributes.value !== 'identifier_first'
    );

    // Extract unique method groups
    const methodGroups = [...new Set(methodButtons.map(node => node.group))];
    
    console.log('Method groups found:', methodGroups);
    console.log('Method buttons found:', methodButtons);

    methodGroups.forEach(group => {
      if (group === 'password') {
        methods.push({
          id: 'password',
          name: 'Password',
          description: 'Sign in with email and password',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          ),
        });
      } else if (group === 'passkey') {
        methods.push({
          id: 'passkey',
          name: 'Passkey',
          description: 'Sign in with passkey',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 16.5H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          ),
        });
      } else if (group === 'code') {
        methods.push({
          id: 'code',
          name: 'Email Code',
          description: 'Send sign in code to email',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          ),
        });
      } else if (group === 'oidc') {
        // Handle OIDC providers
        const oidcNodes = flow.ui.nodes.filter(node => node.group === 'oidc');
        oidcNodes.forEach(node => {
          const provider = node.attributes.value as string;
          methods.push({
            id: `oidc_${provider}`,
            name: `${provider.charAt(0).toUpperCase() + provider.slice(1)}`,
            description: `Sign in with ${provider}`,
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
          });
        });
      }
    });

    console.log('Available methods extracted:', methods);
    return methods;
  }, [flow, step]);

  const handleIdentifierSubmit = async (identifier: string) => {
    if (!flow) return;

    // Same-user fast path: if the active session already belongs to
    // this email, send them straight to the dashboard. Different-user
    // path: log out the existing session BEFORE initiating SSO, so the
    // upstream OIDC flow does not silently reuse it.
    const decision = await ensureCleanSessionFor(identifier);
    if (decision === 'fast_path_same_user') {
      window.location.href = '/';
      return;
    }

    // Step A — SSO discovery. If the email's tenant has SSO enabled, hand
    // the flow off to OIDC immediately by submitting a top-level form to
    // Kratos' action URL with method=oidc + provider=wya-sso +
    // upstream_parameters.login_hint=<loginToken>. Kratos passes that
    // hint to Hydra → SSO Orchestrator, which resolves tenant +
    // connection and proxies through Polis to the customer IdP.
    //
    // If SSO is NOT enabled (or discovery fails / is unconfigured), fall
    // through to the existing Kratos `identifier_first` flow that ends
    // at the method picker.
    try {
      const disco = await discoverSso(identifier);
      if (disco.ssoEnabled && disco.loginToken) {
        console.log('SSO enabled for', identifier, '— handing off to OIDC.');
        // Paint the spinner BEFORE submitting the form. Yielding to a
        // microtask lets React commit the state update and the browser
        // render the new DOM before navigation steals the paint thread.
        setRedirectingToSso(true);
        await Promise.resolve();
        submitOidcLoginForm({
          flowAction: flow.ui.action,
          csrfToken: getCsrfToken(),
          loginToken: disco.loginToken,
        });
        // The browser navigates away. No further state to set.
        return;
      }
      if (disco.enforced) {
        // The tenant requires SSO but discovery did not produce a token —
        // do not let the user fall back to password.
        console.warn('Tenant requires SSO but no loginToken was issued.');
        setShowGenericError(true);
        return;
      }
    } catch (err) {
      console.error('SSO discovery threw, falling back to non-SSO flow:', err);
    }

    // Step B — non-SSO path. Same payload as before: identifier_first
    // submit, then refetch to land on the method picker.
    const payload = {
      method: 'identifier_first',
      identifier: identifier,
      csrf_token: getCsrfToken(),
    };

    console.log('Sending identifier_first payload:', payload);

    try {
      const result = await loginMutation.mutateAsync({
        flow: flow,
        values: payload,
      });

      console.log('Identifier submission result:', result);

      // Store the email for later steps
      setEmail(identifier);

      // Refetch to get the updated flow state
      refetch();
    } catch (error: any) {
      console.log('Identifier submission completed with error response');
      console.log('Error details:', error);

      // Store the email for later steps
      setEmail(identifier);

      // Refetch to get the updated flow state
      // The useEffect will handle the security error detection
      refetch();
    }
  };

  const handleMethodSelect = async (methodId: string) => {
    setSelectedMethod(methodId);
    
    // For methods that can be executed immediately, do so
    if (methodId === 'code') {
      // For code method, submit directly and show result
      setStep('authenticate');
      try {
        await loginMutation.mutateAsync({
          flow: flow!,
          values: {
            method: 'code',
            csrf_token: getCsrfToken(),
          },
        });
      } catch (error) {
        console.error('Code method submission error:', error);
        refetch();
      }
    } else if (methodId === 'passkey') {
      // For passkey, execute immediately and show result
      setStep('authenticate');
      try {
        console.log('Passkey method selected, attempting to trigger WebAuthn...');
        
        // Wait a moment for the DOM to be ready and script to be loaded
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Check what functions are available on window
        console.log('Available window functions:', Object.keys(window).filter(key => key.includes('ory')));
        
        if (window.oryPasskeyLogin) {
          console.log('Using oryPasskeyLogin function');
          window.oryPasskeyLogin();
        } else {
          console.error('oryPasskeyLogin function not available');
          console.log('Window object keys containing "passkey":', Object.keys(window).filter(key => key.toLowerCase().includes('passkey')));
          
          // Try alternative approach - maybe the function has a different name
          if (typeof (window as any).oryPasskeyLoginAutocompleteInit === 'function') {
            console.log('Trying oryPasskeyLoginAutocompleteInit instead');
            (window as any).oryPasskeyLoginAutocompleteInit();
          } else {
            // Show error message to user
            console.error('Passkey authentication is not available');
            setStep('choose_method');
            setSelectedMethod('');
          }
        }
      } catch (error) {
        console.error('Passkey method error:', error);
        setStep('choose_method');
        setSelectedMethod('');
      }
    } else {
      // For password method, show the form
      setStep('authenticate');
    }
  };

  const handleChangeUser = () => {
    setStep('identify');
    setEmail('');
    setSelectedMethod('password');
  };

  const handlePasswordSubmit = async (password: string) => {
    if (!flow) return;

    try {
      await loginMutation.mutateAsync({
        flow: flow,
        values: {
          method: 'password',
          identifier: email,
          password: password,
          csrf_token: getCsrfToken(),
        },
      });
    } catch (error) {
      // Error is handled by the mutation
      // Refetch the flow to get updated error messages
      refetch();
    }
  };

  const handleKratosFormSubmit = async (formData: FormData) => {
    if (!flow) return;

    console.log('Handling Kratos form submission...');
    
    // Convert FormData to object
    const values: Record<string, unknown> = {};
    for (const [key, value] of formData.entries()) {
      values[key] = String(value);
    }

    // Ensure we have a method - for passkey submissions, it should be 'passkey'
    if (!values.method) {
      values.method = selectedMethod || 'passkey';
    }

    console.log('Form values:', values);

    try {
      await loginMutation.mutateAsync({
        flow: flow,
        values: values as LoginValues,
      });
    } catch (error) {
      console.error('Kratos form submission error:', error);
      // Refetch the flow to get updated error messages
      refetch();
    }
  };

  const getCsrfToken = (): string => {
    if (!flow) {
      console.log('No flow available for CSRF token');
      return '';
    }
    const csrfNode = flow.ui.nodes.find(node => 
      node.attributes.name === 'csrf_token'
    );
    const token = String(csrfNode?.attributes.value || '');
    console.log('CSRF token extracted:', token ? 'Found' : 'Not found', csrfNode);
    return token;
  };

  // Show loading while checking session or redirecting
  if (
    redirectingToSso ||
    checkingSession ||
    !showUi ||
    !flowId ||
    isLoading
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center gap-3 py-8">
            <div
              className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent"
              aria-hidden="true"
            />
            <div className="text-sm text-muted-foreground">
              {redirectingToSso
                ? 'Redirecting to your identity provider…'
                : checkingSession
                ? 'Checking session...'
                : !flowId
                ? 'Redirecting to login...'
                : 'Loading...'}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !flow) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Error</CardTitle>
            <CardDescription>Failed to load login form</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
            </p>
            <Button onClick={() => refetch()} className="w-full">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Sign In</CardTitle>
          <CardDescription>
            Welcome back! Please sign in to your account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Render Kratos form - required for passkey authentication */}
          {flow && (
            <form 
              id="kratos-form"
              action={flow.ui.action}
              method={flow.ui.method}
              style={{ display: 'none' }}
              onSubmit={(e) => {
                e.preventDefault();
                console.log('Kratos form submitted - handling via React');
                // Handle form submission via our React logic
                handleKratosFormSubmit(new FormData(e.currentTarget));
              }}
            >
              {flow.ui.nodes
                .filter(node => node.type === 'input')
                .map((node, index) => {
                  const attrs = node.attributes;
                  return (
                    <input
                      key={index}
                      type={attrs.type as string}
                      name={attrs.name as string}
                      value={String(attrs.value || '')}
                      disabled={attrs.disabled as boolean}
                      required={attrs.required as boolean}
                      readOnly={true}
                      style={{ display: attrs.type === 'hidden' ? 'none' : 'block' }}
                    />
                  );
                })}
            </form>
          )}

          {/* Security-aware error messages */}
          {(showGenericError || (flow?.ui.messages && flow.ui.messages.length > 0)) && (
            <div className="space-y-2">
              {/* Generic error for security (prevents user enumeration) */}
              {showGenericError && (
                <div className="p-3 text-sm rounded-md text-destructive bg-destructive/10 border border-destructive/20">
                  <div>Invalid email or password. Please check your credentials and try again.</div>
                  <div className="mt-2 pt-2 border-t border-destructive/20">
                    <p className="text-xs text-muted-foreground">
                      Don't have an account?{' '}
                      <Link 
                        to="/auth/registration" 
                        className="text-primary hover:underline font-medium"
                      >
                        Create one here
                      </Link>
                    </p>
                  </div>
                </div>
              )}
              
              {/* Kratos messages (filtered for security) */}
              {flow?.ui.messages && flow.ui.messages
                .filter(message => {
                  // Filter out account enumeration errors for security
                  if (message.id === 4000037) {
                    // "This account does not exist or has no login method configured"
                    // Don't show this to prevent user enumeration
                    return false;
                  }
                  return true;
                })
                .map((message, index) => (
                  <div 
                    key={index}
                    className={`p-3 text-sm rounded-md ${
                      message.type === 'error' 
                        ? 'text-destructive bg-destructive/10 border border-destructive/20'
                        : message.type === 'success'
                        ? 'text-green-700 bg-green-50 border border-green-200'
                        : 'text-blue-700 bg-blue-50 border border-blue-200'
                    }`}
                  >
                    {message.text}
                  </div>
                ))}
            </div>
          )}

          {/* Step 1: Identifier Input */}
          {step === 'identify' && (
            <>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Enter your email address to get started
                </p>
                <IdentifierInput
                  value=""
                  onChange={() => {
                    // Clear generic error when user starts typing
                    if (showGenericError) {
                      setShowGenericError(false);
                    }
                  }}
                  onSubmit={handleIdentifierSubmit}
                  disabled={loginMutation.isPending}
                  placeholder="Enter your email"
                />
              </div>
            </>
          )}

          {/* Step 2: Method Selection */}
          {step === 'choose_method' && (
            <>
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    Signed in as: <span className="font-medium">{email}</span>
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleChangeUser}
                    className="text-xs"
                  >
                    Change user
                  </Button>
                </div>
                
                <div className="space-y-3">
                  <div className="text-sm font-medium text-foreground">
                    Choose authentication method:
                  </div>
                  <div className="grid gap-2">
                    {availableMethods.map((method) => (
                      <Button
                        key={method.id}
                        variant="outline"
                        className="flex items-center justify-start space-x-3 p-4 h-auto"
                        onClick={() => handleMethodSelect(method.id)}
                        disabled={loginMutation.isPending}
                      >
                        <div className="flex-shrink-0">
                          {method.icon}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="text-sm font-medium">
                            {method.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {method.description}
                          </div>
                        </div>
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Step 3: Authentication Form */}
          {step === 'authenticate' && (
            <>
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    Continuing as: <span className="font-medium">{email}</span>
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleChangeUser}
                    className="text-xs"
                  >
                    Change user
                  </Button>
                </div>

                {/* Password form */}
                {selectedMethod === 'password' && (
                  <PasswordForm 
                    onSubmit={handlePasswordSubmit}
                    disabled={loginMutation.isPending}
                  />
                )}

                {/* Show result message after method selection */}
                {selectedMethod === 'code' && (
                  <div className="text-center space-y-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-700 font-medium">
                        Sign-in code sent!
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        Please check your email and click the link to complete sign in
                      </p>
                    </div>
                  </div>
                )}
                
                {selectedMethod === 'passkey' && (
                  <div className="text-center space-y-4">
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-sm text-blue-700 font-medium">
                        Passkey authentication initiated
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        Please follow your browser's prompts to complete authentication
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      onClick={() => setStep('choose_method')}
                      className="text-sm"
                    >
                      Try another method
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Links to other auth pages - only show in identify step */}
          {step === 'identify' && (
            <div className="text-center space-y-2">
              <div className="text-sm">
                <Link 
                  to="/auth/recovery" 
                  className="text-primary hover:underline"
                >
                  Forgot your password?
                </Link>
              </div>
              <div className="text-sm text-muted-foreground">
                Don't have an account?{' '}
                <Link 
                  to="/auth/registration" 
                  className="text-primary hover:underline"
                >
                  Sign up
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
